import {AnyAction, createAction, createAsyncThunk, Dispatch, ThunkDispatch} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'
import {qs} from '@shared/helpers/queryString'

import {selectPayMethod} from '@web/actions/priorityPaymentMethod'
import {
    PayMethod,
    PAYMONEY_RESULT_TYPE,
    SubscribePayMethodType,
    PaymoneyResultType,
    ModalCase,
} from '@web/core/constants'
import {
    availableRechargePointPayMethodSelector,
    pointTogglePaymentSelector,
} from '@web/selectors/priorityPaymentMethodInfo'
import {
    adapterChargePointSelector,
    AdapterState,
    chargePointSelector,
    ChargePointState,
    payMoneyInfoSelector,
} from '@web/slices/chargePoint/slice'
import {CommonState, entryToServiceSequence, pageSetInfoSelector} from '@web/slices/common/slice'
import {ExpenseDetailsState} from '@web/slices/expenseDetails/slice'
import {ModalState, openModal} from '@web/slices/modal/slice'
import {PointPaymentState} from '@web/slices/pointPayment/slice'
import {
    isRegisteredEasyPayCreditCardSelector,
    prevSelectedMethodSelector,
    PriorityPaymentMethodState,
    selectedMethodSelector,
} from '@web/slices/priorityPaymentMethod/slice'
import {PurchaserState} from '@web/slices/purchaser/slice'
import {RewardPointState} from '@web/slices/rewardPoint/slice'

import {benefitApi} from './apis'
import {PayChannel, PaymoneyAndRewardResponse, RewardPoint, RewardPointBalance} from './interfaces'

const RETRYING_POINT_AMOUNT_TIMEOUT = 3000

type PayloadStateType = {
    rechargePoint: {
        rechargingAmount: number
        enable: boolean
    }
    selectedPayMethod: SubscribePayMethodType
}

const createPayloadState = (state: AdapterState): PayloadStateType => {
    return {
        rechargePoint: {
            rechargingAmount: state.rechargePoint?.rechargingAmount ?? 0,
            enable: state.rechargePoint?.rechargePointEnabled ?? false,
        },
        selectedPayMethod: state.selectedMethod,
    }
}

/**
 * 충전결제 사용 Action
 * - 선택시 전액 적용
 * - 해제시 0원 적용
 * - 점검중시 0원 적용
 **/
export const enableRechargePoint = createAction(
    'pointPayment/enableRechargePoint',
    (enable: boolean, adapterState: AdapterState) => ({
        payload: createPayloadState({
            ...adapterState,
            rechargePoint: {
                ...adapterState.rechargePoint,
                rechargingAmount: 0,
                rechargePointEnabled: enable,
            },
        }),
    }),
)

type RootState = {
    common: CommonState
    chargePoint: ChargePointState
    expenseDetails: ExpenseDetailsState
    modal: ModalState
    pointPayment: PointPaymentState
    priorityPaymentMethod: PriorityPaymentMethodState
    purchaser: PurchaserState
    rewardPoint: RewardPointState
}

export type ThunkApi<RejectValue = unknown> = {
    state: RootState
    dispatch: ThunkDispatch<RootState, unknown, AnyAction> & Dispatch<AnyAction>
    rejectValue: RejectValue
}

/**
 * 네이버 포인트 사용 action
 **/
export const enableNaverPoint = createAsyncThunk<void, boolean, ThunkApi>(
    'pointPayment/enableNaverPoint',
    async (checked, {dispatch, getState, rejectWithValue}) => {
        if (!checked) {
            const state = getState()
            const nextState = {
                ...state,
                pointPayment: {
                    ...state.pointPayment,
                    naverPayPoint: {...state.pointPayment.naverPayPoint, usePointAll: false},
                },
            }
            const selectedMethod = selectedMethodSelector(state)
            const {isPayMoneyUser, chargeLimitResultType} = payMoneyInfoSelector(state)
            const hasCreditCard = isRegisteredEasyPayCreditCardSelector(state)
            const {
                reloadable: {limit: limitedAmount, amount: reloadableRechargePointAmount},
            } = chargePointSelector(state)
            /** 아래에서 사용하는 selector 들은 usePointAll state 값이 현재와 다른 값으로 계산되어야함 */
            const {expectedRechargePointAmount} = pointTogglePaymentSelector(nextState)
            const availableRechargePointPayMethod = availableRechargePointPayMethodSelector(nextState)
            /** 충전한도 API 성공 여부 */
            const isChargeLimitRequestSucceed = eq(chargeLimitResultType, PAYMONEY_RESULT_TYPE.SUCCESS)

            /** 계속 선택 시 카드간편 선택, 카드 미등록 시 계좌간편 선택 */
            const nextPayment = hasCreditCard ? PayMethod.CARD_EASY_PAY : PayMethod.BANK_EASY_PAY
            const CAN_NOT_USE_RECHARGE_POINT_MESSAGE =
                '포인트 전액사용 OFF 설정 시, 충전한도가 부족하여 충전결제를 사용할 수 없습니다.'

            if (selectedMethod === PayMethod.RECHARGE_POINT) {
                if (isChargeLimitRequestSucceed && limitedAmount && expectedRechargePointAmount > limitedAmount) {
                    /** 포인트 사용 off 시 월 충전한도를 초과하는 경우 */
                    alert(CAN_NOT_USE_RECHARGE_POINT_MESSAGE)
                    await Promise.all([
                        dispatch(enableRechargePoint(false, adapterChargePointSelector(getState()))),
                        dispatch(selectPayMethod(nextPayment, adapterChargePointSelector(getState()))),
                    ])
                    /** [TODO] 아래 조건문을 위의 조건문과 합치고 0823 대상 리그레션 QA 필요 */
                } else if (availableRechargePointPayMethod) {
                    alert(CAN_NOT_USE_RECHARGE_POINT_MESSAGE)
                    /** 충전결제 선택 && aml cdd 이행 대상 유저 && 보유 + 예상 충전 금액이 50만원 이상인 경우 */
                    const prevSelectedMethod = prevSelectedMethodSelector(state)
                    /** 충전결제 선택을 취소하고 이전 결제수단을 선택하거나, 이전결제수단이 없으면 카드간편결제를 선택한다 */
                    dispatch(selectPayMethod(prevSelectedMethod, adapterChargePointSelector(getState())))
                } else if (
                    !isPayMoneyUser &&
                    isChargeLimitRequestSucceed &&
                    expectedRechargePointAmount > reloadableRechargePointAmount
                ) {
                    /** 페이머니 회원이 아니고 && 충전결제 선택했고 && 결제 예상 금액이 충전가능 금액보다 클 경우 */
                    dispatch(openModal(ModalCase.RECHARGE_PAYMENT_UNABLE_BY_POINT_TOGGLE))
                    return rejectWithValue({error: '사용자 확인 후 포인트 사용 OFF'})
                }
            }
        }
        return undefined
    },
)

type RewardPointPayload = PaymoneyAndRewardResponse['reward']

type ChargePointPayload = Pick<
    PaymoneyAndRewardResponse['paymoney'],
    'availableAmount' | 'balanceAmount' | 'resultType'
>

export const getChargePointBalanceBenefit = createAsyncThunk<
    ChargePointPayload,
    void,
    ThunkApi<{resultType: PaymoneyResultType}>
>('pointPayment/getChargePointBalance', async (_, {rejectWithValue}) => {
    const {paymoney} = await benefitApi.fetchPaymoneyAndReward({
        timeout: RETRYING_POINT_AMOUNT_TIMEOUT,
    })

    if (paymoney.resultType !== PAYMONEY_RESULT_TYPE.SUCCESS) {
        return rejectWithValue({resultType: paymoney.resultType})
    }

    return paymoney
})

export type PaymoneyAndRewardPayload = {
    paymoney: ChargePointPayload
    reward: RewardPointPayload
}

export const getPaymoneyAndRewardBenefit = createAsyncThunk<
    PaymoneyAndRewardPayload,
    void,
    ThunkApi<{
        rewardResultType: PaymoneyResultType
        paymoneyResultType: PaymoneyResultType
    }>
>('pointPayment/getPaymoneyAndReward', async (_, {getState, rejectWithValue}) => {
    const state = getState()
    const {isPaymentMethodManagement, isDiconGroup} = pageSetInfoSelector(state)
    const payChannel = (() => {
        if (isDiconGroup) {
            return PayChannel.DICON
        }
        return null
    })()
    const {reward, paymoney} = await benefitApi.fetchPaymoneyAndReward({
        timeout: RETRYING_POINT_AMOUNT_TIMEOUT,
        params: {
            allowPartialBalanceYn: true,
            type: isPaymentMethodManagement
                ? RewardPointBalance.INCLUDE_TOTAL_VOUCHER
                : RewardPointBalance.INCLUDE_VOUCHER,
            include: [RewardPoint.WELFARE],
            ...(payChannel ? {payChannel} : {}),
            ...(payChannel === 'DICON' ? {serviceSeq: entryToServiceSequence(state.common.entry)} : {}),
        },
        paramsSerializer: (params) => {
            return qs.stringify(params, {arrayFormat: 'none'})
        },
    })

    const rewardResultType = reward.resultType
    const paymoneyResultType = paymoney.resultType
    const eqResultSuccess = eq(PAYMONEY_RESULT_TYPE.SUCCESS)

    if (!eqResultSuccess(rewardResultType) && !eqResultSuccess(paymoneyResultType)) {
        return rejectWithValue({
            rewardResultType: reward.resultType,
            paymoneyResultType: paymoney.resultType,
        })
    }

    return {reward, paymoney}
})
